import { Injectable, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import {
  NavContext,
  NavItem,
  PrimaryAction,
  ResolvedNavItem,
  ResolvedPrimaryAction,
} from './nav.models';
import { NAV_ITEMS, PRIMARY_ACTIONS } from './navigation.tokens';
import { mergedParams, resolveRoute, routeChain } from './route-snapshot';

const MAX_MOBILE_ITEMS = 4;

/**
 * Le voci da mostrare: filtra quelle dichiarate per il contesto della rotta attiva
 * (`data.navContext`, ereditato dalle rotte figlie), le ordina e risolve i segnaposto come
 * `:siteId` dai parametri attivi (research R-15).
 */
@Injectable({ providedIn: 'root' })
export class NavigationService {
  private readonly router = inject(Router);
  private readonly declared: readonly NavItem[] = inject(NAV_ITEMS, { optional: true }) ?? [];
  private readonly actions: readonly PrimaryAction[] =
    inject(PRIMARY_ACTIONS, { optional: true }) ?? [];

  private readonly state = signal<{ context: NavContext; params: Record<string, string> }>({
    context: 'home',
    params: {},
  });

  readonly context = computed(() => this.state().context);

  /** Tutte le voci del contesto, ordinate e risolte. */
  readonly items = computed<ResolvedNavItem[]>(() => {
    const { context, params } = this.state();
    return this.declared
      .filter((item) => item.context === context && (item.visible?.() ?? true))
      .map((item) => ({ ...item, link: resolveRoute(item.route, params) }))
      .filter((item): item is ResolvedNavItem => item.link !== null)
      .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label, 'it'));
  });

  readonly mainItems = computed(() => this.items().filter((item) => item.section === 'main'));
  readonly appItems = computed(() => this.items().filter((item) => item.section === 'apps'));
  readonly secondaryItems = computed(() =>
    this.items().filter((item) => item.section === 'secondary'),
  );
  /** Voci della barra in basso: al massimo quattro, poi "Altro". */
  readonly mobileItems = computed(() =>
    this.items()
      .filter((item) => item.mobile)
      .slice(0, MAX_MOBILE_ITEMS),
  );

  readonly primaryAction = computed<ResolvedPrimaryAction | null>(() => {
    const { context, params } = this.state();
    const action = this.actions.find((item) => item.context === context);
    if (!action) {
      return null;
    }
    return { ...action, link: action.route ? resolveRoute(action.route, params) : null };
  });

  constructor() {
    this.update();
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.update());
  }

  private update(): void {
    const chain = routeChain(this.router.routerState.snapshot.root);
    let context: NavContext = 'home';
    for (const route of chain) {
      const declared = route.routeConfig?.data?.['navContext'] as NavContext | undefined;
      if (declared === 'home' || declared === 'site') {
        context = declared;
      }
    }
    this.state.set({ context, params: mergedParams(chain) });
  }
}
